import React, { useState } from 'react';
import { Typography, Container, Box, Button, Grid, Card, CardContent, CardMedia } from '@mui/material';
import { styled } from '@mui/material/styles';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import productHero from '../assets/product/product.jpg';
import thermopiedLogo from '../assets/product/thermopied.jpg';
import product1 from '../assets/product/product1.jpg';
import product2 from '../assets/product/product2.jpg';
import product3 from '../assets/product/product3.jpg';
import product4 from '../assets/product/product4.jpg';
import product5 from '../assets/product/product5.jpg';
import product6 from '../assets/product/product6.jpg';
import product7 from '../assets/product/product7.jpg';
import specPDF from '../assets/product/ThermoPied PRODUCT SPECIFICATIONS.pdf';
import { useLanguage } from '../LanguageContext';
import { translations } from '../translations';
import PdfViewerModal from '../components/PdfViewerModal';

const ProductCard = styled(Card)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    borderRadius: theme.spacing(2),
    overflow: 'hidden',
    '&:hover': {
        transform: 'translateY(-12px) scale(1.02)',
        boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
    }
}));

const ProductView = () => {
    const { language } = useLanguage();
    const t = translations[language].product;
    const [modalOpen, setModalOpen] = useState(false);

    const productImages = [
        { src: product1, alt: 'ThermoPied Product View 1' },
        { src: product4, alt: 'ThermoPied Product View 4' },
        { src: product5, alt: 'ThermoPied Product View 5' },
        { src: product6, alt: 'ThermoPied Product View 6' },
      
    ];

    const handleOpenPdf = () => {
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    return (
        <Box sx={{ pt: 4, px: 2 }}>
            {/* Product Section */}
            <Container maxWidth="lg" sx={{ mb: 10, px: { xs: 0, sm: 2 } }}>
                <Card sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.1)',
                    position: 'relative',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                    }
                }}>
                    {/* Image Section */}
                    <Box sx={{
                        flex: { xs: '0 0 auto', md: '0 0 50%' },
                        position: 'relative',
                        bgcolor: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: { xs: 1, md: 3 }
                    }}>
                        <CardMedia
                            component="img"
                            image={productHero}
                            alt="ThermoPied Product"
                            sx={{
                                width: '100%',
                                height: { xs: '350px', md: 'auto' },
                                maxHeight: { md: '500px' },
                                objectFit: 'contain'
                            }}
                        />
                    </Box>

                    {/* Text Section */}
                    <CardContent sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        p: { xs: 3, md: 5 }
                    }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                            <Box
                                component="img"
                                src={thermopiedLogo}
                                alt={t.title || 'ThermoPied'}
                                sx={{ maxWidth: { xs: '180px', md: '260px' }, width: '100%', height: 'auto' }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                bgcolor: 'linear-gradient(135deg, rgba(255,107,107,0.08), rgba(255,107,107,0.04))',
                                px: 3,
                                py: 1.25,
                                borderRadius: 3,
                                boxShadow: '0 6px 18px rgba(0,0,0,0.06)'
                            }}>
                                <Typography sx={{ color: '#FF6B6B', fontWeight: 900, fontSize: { xs: '1.6rem', md: '2rem' }, lineHeight: 1 }}>
                                    <Box component="span" sx={{ fontSize: { xs: '1rem', md: '1.15rem' }, verticalAlign: 'super', mr: 0.3 }}>$</Box>
                                    360
                                </Typography>
                                <Typography sx={{ color: 'text.secondary', fontWeight: 600, fontSize: { xs: '0.8rem', md: '0.95rem' } }}>
                                    CAD
                                </Typography>
                            </Box>
                        </Box>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ mb: 4, lineHeight: 1.8 }}
                        >
                            {t.productDescription}
                        </Typography>

                        {/* What's Included Section */}
                        <Box sx={{
                            bgcolor: 'rgba(33, 150, 243, 0.05)',
                            borderRadius: 2,
                            p: 3,
                            border: '1px solid rgba(33, 150, 243, 0.2)',
                            textAlign: 'left'
                        }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 600,
                                    color: 'primary.main',
                                    mb: 2,
                                    textAlign: 'left'
                                }}
                            >
                                {t.whatsIncluded}
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Typography variant="body1" sx={{ lineHeight: 1.8, textAlign: 'left' }}>
                                    ✓ {t.heatingElements}
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.8, textAlign: 'left' }}>
                                    ✓ {t.batteries}
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.8, textAlign: 'left' }}>
                                    ✓ {t.chargingCable}
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.8, textAlign: 'left' }}>
                                    ✓ {t.warranty}
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Container>

            {/* Product Gallery */}
            <Container maxWidth="lg" sx={{ mb: 10, px: { xs: 0, sm: 2 } }}>
                <Typography
                    variant="h3"
                    component="h2"
                    align="center"
                    gutterBottom
                    sx={{
                        fontWeight: 700,
                        mb: 6,
                        fontSize: { xs: '2rem', md: '2.75rem' }
                    }}
                >
                    {t.productGallery}
                </Typography>

                <Grid container spacing={2}>
                    {productImages.map((image, index) => (
                        <Grid size={{ xs: 6, sm: 4, md: 3 }} key={index}>
                            <ProductCard>
                                <CardMedia
                                    component="img"
                                    image={image.src}
                                    alt={image.alt}
                                    sx={{
                                        height: { xs: 150, sm: 180, md: 200 },
                                        objectFit: 'contain',
                                        backgroundColor: '#f5f5f5'
                                    }}
                                />
                            </ProductCard>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* Specifications Section */}
            <Container maxWidth="xl" sx={{ mb: 10, px: { xs: 0, sm: 2 } }}>
                <Typography
                    variant="h3"
                    component="h2"
                    align="center"
                    gutterBottom
                    sx={{
                        fontWeight: 700,
                        mb: 3,
                        fontSize: { xs: '2rem', md: '2.75rem' }
                    }}
                >
                    {t.technicalSpecs}
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
                    <Button
                        variant="contained"
                        startIcon={<PictureAsPdfIcon />}
                        onClick={handleOpenPdf}
                        sx={{
                            background: 'linear-gradient(135deg, #1565C0 0%, #1976D2 100%)',
                            color: 'white',
                            px: 4,
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600,
                            borderRadius: 2,
                            textTransform: 'none',
                            boxShadow: '0 4px 15px rgba(21, 101, 192, 0.4)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #1976D2 0%, #1565C0 100%)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 20px rgba(21, 101, 192, 0.5)',
                            }
                        }}
                    >
                        {t.viewSpecs}
                    </Button>
                </Box>
            </Container>

            {/* CTA Section */}
            <Box sx={{
                background: 'linear-gradient(135deg, #1565C0 0%, #1976D2 100%)',
                py: { xs: 4, md: 5 },
                textAlign: 'center',
                color: 'white'
            }}>
                <Container maxWidth="sm">
                    <Typography variant="h4" gutterBottom fontWeight={700} sx={{ fontSize: { xs: '1.75rem', md: '2rem' } }}>
                        {t.ctaTitle}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, opacity: 0.9, fontSize: { xs: '1rem', md: '1.1rem' } }}>
                        {t.ctaDescription}
                    </Typography>
                    <Button
                        variant="contained"
                        size="medium"
                        href="/usermanual"
                        sx={{
                            px: 4,
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 700,
                            bgcolor: 'white',
                            color: 'primary.main',
                            borderRadius: 2,
                            textTransform: 'none',
                            '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.9)',
                                transform: 'translateY(-2px)',
                            }
                        }}
                    >
                        {t.learnMore}
                    </Button>
                </Container>
            </Box>

            {/* PDF Modal */}
            <PdfViewerModal
                open={modalOpen}
                onClose={handleCloseModal}
                pdfUrl={specPDF}
                title={t.technicalSpecs}
            />
        </Box>
    );
};

export default ProductView;




